import { useMemo } from 'react';
import { useAppSelector } from '@/app/hooks';
import { useSiiAvanceControllerReadQuery } from '@/services/generated/api';
import {
  CERTIFICATION_STAGES,
  type CertificationStage,
} from '@/features/certification/avance/api';
import {
  CERTIFICATION_STAGE_LABELS,
  CERTIFICATION_STAGE_SCREENS,
} from '@/features/onboarding/certification-stage';
import type { NavChild, NavGroup, NavItem } from './types';

/**
 * The step the SII says the active company is on, or `null` when it is not
 * known: no company, no permission, a company already in production, a read
 * that failed, or a stage Factit only estimated (`source: 'local'`). Without a
 * reading from the SII nothing is locked, because a lock Factit guessed could
 * stand between a person and a step that is in fact open.
 *
 * It shares its cache entry with «Estado de certificación» and «Declarar
 * avance», so it costs no request of its own once one of them is open, and the
 * mutations that move the step (`SiiAvance` tag) refresh it.
 */
export function useCertificationStage(
  enabled: boolean,
): CertificationStage | null {
  const companyId = useAppSelector((state) => state.auth.activeCompanyId);
  const query = useSiiAvanceControllerReadQuery(
    { companyId: companyId ?? '' },
    { skip: !enabled || !companyId },
  );
  const stage = query.data?.stage;
  return stage && stage.source === 'sii' ? stage.current : null;
}

/** Which SII step a menu destination is, by the screen it opens. */
const STAGE_BY_PATH: ReadonlyMap<string, CertificationStage> = new Map(
  CERTIFICATION_STAGES.flatMap((stage): [string, CertificationStage][] => {
    const to = CERTIFICATION_STAGE_SCREENS[stage];
    return to === null ? [] : [[to, stage]];
  }),
);

/** What unlocks a step: the one before it, said by its name. */
export function lockReasonFor(stage: CertificationStage): string {
  const index = CERTIFICATION_STAGES.indexOf(stage);
  const previous = CERTIFICATION_STAGES[index - 1];
  return previous
    ? `Completa «${CERTIFICATION_STAGE_LABELS[previous]}» para habilitarlo`
    : '';
}

/**
 * Marks every certification step against the one the SII says the company is
 * on: the ones behind it `done`, that one `current`, and the ones after it
 * locked with what unlocks them. The current step and the ones behind it stay
 * open (a person can go back to look), the status and the company prerequisite
 * are neither marked nor locked, and with `current === null` the tree is
 * returned as it came, the SAME reference, because `AppShell` has effects that
 * depend on `nav`'s identity. A stage Factit only estimated never gets here
 * (`useCertificationStage`), so no marker claims a step the SII has not passed.
 */
export function withCertificationLocks(
  groups: readonly NavGroup[],
  current: CertificationStage | null,
): readonly NavGroup[] {
  if (current === null) return groups;
  const currentIndex = CERTIFICATION_STAGES.indexOf(current);
  let changed = false;
  const next = groups.map((group) => {
    const items = group.items.map((item): NavItem => {
      if (!item.children) return item;
      const children = item.children.map((child): NavChild => {
        const stage = STAGE_BY_PATH.get(child.to);
        if (stage === undefined) return child;
        changed = true;
        const index = CERTIFICATION_STAGES.indexOf(stage);
        if (index < currentIndex) return { ...child, progress: 'done' };
        if (index === currentIndex) return { ...child, progress: 'current' };
        return { ...child, blockReason: lockReasonFor(stage) };
      });
      return children.some((c, i) => c !== item.children?.[i])
        ? { ...item, children }
        : item;
    });
    return items.some((it, i) => it !== group.items[i])
      ? { ...group, items }
      : group;
  });
  return changed ? next : groups;
}

/** The nav groups with the certification locks applied. Memoized on the stage. */
export function useCertificationLocks(
  groups: readonly NavGroup[],
  enabled: boolean,
): readonly NavGroup[] {
  const stage = useCertificationStage(enabled);
  return useMemo(() => withCertificationLocks(groups, stage), [groups, stage]);
}
